import React, { useCallback, useEffect, useState } from "react";
import { Button, Dimmer, Loader, Message, Table } from "semantic-ui-react";
import styled from "styled-components";
import { elevation, palette } from "@conserveit/blocks";
import { Trans } from "@lingui/macro";
import { ModalState } from "./index";
import SaveModal from "./SaveModal";
import DiscardModal from "./DiscardModal";
import EditableColumn from "./EditableColumn";
import {
  getTemplateDetails,
  postTemplateDetails,
  Templates,
} from "../../../api/siteDetailsApi";
import SiteDropdown from "./components/SiteDropdown";
import EditableDropdown from "./EditableDropdown";

const Wrapper = styled.div`
  padding-left: 50px;
  padding-right: 50px;
  width: 100%;
`;

const StyledMessage = styled(Message)`
  width: 100%;
  margin: 0;
`;

const TableShadow = styled.div`
  border-radius: 5px;
  box-shadow: ${elevation.content};
`;

const StyledTable = styled(Table)`
  background-color: green;
  margin: 0 !important;
`;

const StyledHeader = styled(Table.Header)`
  font-size: 13px;
  text-align: center;
  vertical-align: middle;
`;

const StyledRow = styled(Table.Row)`
  text-align: center;
  justify-content: center;
  vertical-align: middle;
`;

const StyledCell = styled(Table.HeaderCell)`
  align-items: center;
  vertical-align: middle;
`;

const StyledData = styled(Table.Cell)`
  align-items: center;
  vertical-align: middle;
  height: 70px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ButtonMargin = styled.div`
  margin: 5px 35px 5px 35px;
`;

const DiscardButton = styled(Button)`
  width: 100px;
  padding-left: 0 !important;
  padding-right: 0 !important;
  color: ${palette.pureWhite} !important;
  background-color: ${palette.danger} !important;
`;

const SaveButton = styled(Button)`
  width: 100px;
  padding-left: 0 !important;
  padding-right: 0 !important;
  color: ${palette.pureWhite} !important;
  background-color: ${palette.success} !important;
`;

const EditButton = styled(Button)`
  width: 100px;
  padding-left: 0 !important;
  padding-right: 0 !important;
  color: ${palette.pureWhite} !important;
  background-color: ${palette.edit} !important;
`;

const tableColumns: Templates[] = [];

interface Props {
  sites: Insite.AppState["userSites"]["sites"];
  setCurrentSite: (syncoId: string) => void;
  currentSite: string;
}

const APITable: React.FC<Props> = ({ sites, setCurrentSite, currentSite }) => {
  const [prevTableData, setPrevTableData] = useState<Templates[]>(tableColumns);
  const [tableData, setTableData] = useState<Templates[]>(tableColumns);
  const [errorHandlingMessage, setErrorHandlingMessage] = useState<String>("");
  const [errorHandlingEndPoint, setErrorHandlingEndPoint] = useState<String>("");

  // Using blank space naming convention for unused destructured variables
  // We will use 'error' for toaster notification and 'loading' for loading animation in the future
  const [loading, setLoading] = useState<boolean>(true);
  const [errorState, setError] = useState<boolean>(false);

  const [isTableEditable, setIsTableEditable] = useState<boolean>(false);
  const [modalState, setModalState] = useState<ModalState>("HIDE");

  const getData = useCallback(() => {
    setLoading(true);
    getTemplateDetails(
      currentSite,
      (data) => {
        setPrevTableData(data);
        setTableData(data);
        setLoading(false);
        setError(false);
      },
      (errorResponse) => {
        setErrorHandlingMessage(errorResponse.data);
        setErrorHandlingEndPoint(errorResponse.url);
        setError(true);
        setLoading(false);
      }
    );
  }, [
    getTemplateDetails,
    currentSite,
    setPrevTableData,
    setTableData,
    setErrorHandlingMessage,
    setLoading,
    setError,
  ]);
  useEffect(() => {
    getData();
  }, [getData]);

  const onModalDiscard = () => {
    setIsTableEditable(false);
    setModalState("HIDE");
    setTableData(prevTableData);
  };

  const onModalConfirm = () => {
    setIsTableEditable(false);
    setModalState("HIDE");
    postTemplateDetails(
      currentSite,
      tableData,
      (response) => {
        setPrevTableData(response);
        setLoading(false);
        setError(false);
      },
      (errorResponse) => {
        setErrorHandlingMessage(errorResponse.data);
        setErrorHandlingEndPoint(errorResponse.url);
        setLoading(false);
        setError(true);
      }
    );
  };

  const onModalCancel = () => {
    setModalState("HIDE");
  };

  const updateTableData = (
    fieldName: string,
    isNumber: boolean,
    index: number,
    content: any
  ) => {
    const tableCopy = [...tableData];
    tableCopy[index] = {
      ...tableCopy[index],
      [fieldName]: isNumber ? Number(content) : content,
    };
    setTableData(tableCopy);
  };

  const resourceTypeOptions=[`CONTROLS`, `MECHANICAL`, 'OPTIMISATION_ENGINEER', 'DEPLOYMENT_ENGINEER']

  return (
    <>
      <Wrapper>
        <SiteDropdown
          sites={sites}
          setCurrentSite={setCurrentSite}
          currentSite={currentSite}
          disabled={isTableEditable}
        />
        {!errorState && (
          <TableShadow>
            <Dimmer.Dimmable dimmed={loading}>
              <Dimmer active={loading} inverted>
                <Loader>Loading</Loader>
              </Dimmer>
              <StyledTable celled>
                <StyledHeader>
                  <StyledRow>
                    <StyledCell width="1">
                      <Trans id="site_details.table.unique_id_number">ID</Trans>
                    </StyledCell>
                    <StyledCell width="5">
                      <Trans id="site_details.table.rule_summary">
                        Rule Summary
                      </Trans>
                    </StyledCell>
                    <StyledCell width="2">
                      <Trans id="site_details.table.resource_type">
                        Resource Type
                      </Trans>
                    </StyledCell>
                    <StyledCell width="2">
                      <Trans id="site_details.table.impact_on_equipment">
                        Impact on Equipment
                      </Trans>
                    </StyledCell>
                    <StyledCell width="2">
                      <Trans id="site_details.table.impact_on_occupants">
                        Impact on Occupants
                      </Trans>
                    </StyledCell>
                    <StyledCell width="2">
                      <Trans id="site_details.table.energy_saving">
                        Energy Saving (kWh)
                      </Trans>
                    </StyledCell>
                    <StyledCell width="2">
                      <Trans id="site_details.table.rule_status">
                        Rule Status
                      </Trans>
                    </StyledCell>
                  </StyledRow>
                </StyledHeader>

                <Table.Body>
                  {tableData.map((data, index) => {
                    return (
                      <StyledRow key={data.templatePk}>
                        <StyledData data-testid={`table__id_${index}`}>
                          {data.templatePk}
                        </StyledData>
                        <StyledData
                          data-testid={`table__analytic-rule_${index}`}
                        >
                          {data.sparkTypeEntity.sparkTypeName}
                        </StyledData>
                        <StyledData>
                        <EditableDropdown
                          data-testid={`table__resource-type_${index}`}
                          content={resourceTypeOptions[data.resourceType]}
                          isTableEditable={isTableEditable}
                          options={resourceTypeOptions}
                          onChange={(content: string) => {
                            var contentParsed
                            if (content == resourceTypeOptions[0])
                            {
                              contentParsed = 0
                            }
                            else if (content == resourceTypeOptions[1])
                            {
                              contentParsed = 1
                            }
                            if (content == resourceTypeOptions[2])
                            {
                              contentParsed = 2
                            }
                            if (content == resourceTypeOptions[3])
                            {
                              contentParsed = 3
                            }
                            return updateTableData(
                              "resourceType",
                              false,
                              index,
                              contentParsed
                            );
                          }}
                        />
                      </StyledData>
                        <StyledData>
                          <EditableColumn
                            data-testid={`table__impact-equip_${index}`}
                            content={data.impactEquip}
                            isTableEditable={isTableEditable}
                            onChange={(content: string) =>
                              updateTableData(
                                "impactEquip",
                                true,
                                index,
                                content
                              )
                            }
                          />
                        </StyledData>
                        <StyledData>
                          <EditableColumn
                            data-testid={`table__impact-comfort_${index}`}
                            content={data.impactComfort}
                            isTableEditable={isTableEditable}
                            onChange={(content: string) =>
                              updateTableData(
                                "impactComfort",
                                true,
                                index,
                                content
                              )
                            }
                          />
                        </StyledData>
                        <StyledData>
                          <EditableColumn
                            data-testid={`table__energy-saving_${index}`}
                            content={data.estimatedSavings}
                            isTableEditable={isTableEditable}
                            onChange={(content: string) =>
                              updateTableData(
                                "estimatedSavings",
                                true,
                                index,
                                content
                              )
                            }
                          />
                        </StyledData>
                        <StyledData>
                          <EditableDropdown
                            data-testid={`table__rule-status_${index}`}
                            content={
                              data.enabled === true
                                ? "Enabled"
                                : "Disabled"
                            }
                            isTableEditable={isTableEditable}
                            options={["Enabled", "Disabled"]}
                            onChange={(content: string) => {
                              const contentParsed =
                                content === "Enabled" ? true : false;
                              return updateTableData(
                                "enabled",
                                false,
                                index,
                                contentParsed
                              );
                            }}
                          />
                        </StyledData>
                      </StyledRow>
                    );
                  })}
                </Table.Body>

                <Table.Footer>
                  <Table.Row>
                    <StyledCell colSpan="12">
                      <ButtonContainer>
                        {!isTableEditable && (
                          <ButtonMargin>
                            <EditButton
                              data-testid="table-edit-button"
                              onClick={() => setIsTableEditable(true)}
                            >
                              <Trans id="site_details.edit">Edit</Trans>
                            </EditButton>
                          </ButtonMargin>
                        )}
                        {isTableEditable && (
                          <>
                            <ButtonMargin>
                              <DiscardButton
                                data-testid="table-discard-button"
                                onClick={() => setModalState("CONFIRM_DISCARD")}
                              >
                                <Trans id="site_details.discard">Discard</Trans>
                              </DiscardButton>
                            </ButtonMargin>
                            <ButtonMargin>
                              <SaveButton
                                data-testid="table-save-button"
                                onClick={() => setModalState("CONFIRM_SAVE")}
                              >
                                <Trans id="site_details.save">Save</Trans>
                              </SaveButton>
                            </ButtonMargin>
                          </>
                        )}
                      </ButtonContainer>
                    </StyledCell>
                  </Table.Row>
                </Table.Footer>
              </StyledTable>
            </Dimmer.Dimmable>
          </TableShadow>
        )}
        {errorState && (
          <StyledMessage>
            <Message.Header>Error</Message.Header>
              { errorHandlingEndPoint !== "undefined" && (
                <p> Request unsuccessful for this endpoint:  {errorHandlingEndPoint} </p>
              )} 
              <p data-testid="api_error_handling_response">{errorHandlingMessage}</p>
              <p>If this error persists, please contact support with the error message and steps to replicate via the support portal or email support@conserveitiot.com.</p>
          </StyledMessage>
        )}
      </Wrapper>

      {modalState === "CONFIRM_SAVE" && (
        <SaveModal
          data-testid="table__confirm-save-modal"
          onModalConfirm={onModalConfirm}
          onModalCancel={onModalCancel}
          message={"site_details.modal.action_confirmation_lose_changes"}
        />
      )}
      {modalState === "CONFIRM_DISCARD" && (
        <DiscardModal
          data-testid="table__confirm-discard-modal"
          onModalConfirm={onModalDiscard}
          onModalCancel={onModalCancel}
        />
      )}
    </>
  );
};

export default APITable;
